import axios, { type AxiosInstance, type InternalAxiosRequestConfig } from 'axios'

import { getIdToken } from './auth'

/**
 * API リクエストに Cognito の ID トークンを付ける。
 *
 * API Gateway の JWT オーソライザは ID トークンの aud を検証するので、
 * アクセストークンではなく ID トークンを送ること。
 *
 * トークンは毎リクエスト取り直す。Amplify が期限切れ間近なら裏で更新するので、
 * 1 時間でセッションが切れることはない（リフレッシュトークンの寿命は 7 日）。
 */
export function attachAuthInterceptors(instance: AxiosInstance = axios): AxiosInstance {
  instance.interceptors.request.use(async (config) => {
    const url = config.url ?? ''
    // 外部 API（zipcloud など）にトークンを漏らさないよう、自前の API だけに付ける。
    // baseURL に /api を持つインスタンスは url が相対パスになるのでそちらも見る。
    const base = config.baseURL ?? ''
    const isOwnApi = url.startsWith('/api') || url.includes('/api/') || base.includes('/api')
    if (!isOwnApi) return config

    const token = await getIdToken()
    if (token) config.headers.set('Authorization', `Bearer ${token}`)
    return config
  })

  instance.interceptors.response.use(
    (response) => response,
    (error: unknown) => {
      const status =
        typeof error === 'object' && error !== null
          ? (error as { response?: { status?: number } }).response?.status
          : undefined

      // セッション切れはログイン画面へ戻す。
      // リフレッシュトークンは 7 日で失効する（infra/stack/Core/cognito.ts）。
      if (status === 401 && window.location.pathname !== '/login') {
        window.location.assign('/login')
      }
      return Promise.reject(error)
    }
  )

  return instance
}

/** リトライ対象。いずれも「Lambda がまだ実行されていない」可能性が高い一時障害。 */
const RETRYABLE_STATUS = [429, 502, 503, 504]
const MAX_RETRIES = 3
const BASE_DELAY_MS = 300

type RetryableConfig = InternalAxiosRequestConfig & { __retryCount?: number }

/**
 * 一時的な 503 を吸収するリトライ。
 *
 * ダッシュボードは 1 画面で十数本の API を並列に叩く。Lambda の同時実行数を超えると
 * リクエストは Lambda に届かないまま弾かれ、API Gateway が 503
 * `{"message":"Service Unavailable"}` を返す。数百 ms 待てば枠が空くので待って投げ直す。
 *
 * 再送するのは GET だけにする。503 は「実行されなかった」と「実行後に応答が返らなかった」の
 * 区別が付かないため、更新系を再送すると二重登録になりうる。
 */
export function attachRetryInterceptor(instance: AxiosInstance = axios): AxiosInstance {
  instance.interceptors.response.use(undefined, async (error: unknown) => {
    const err = error as {
      config?: RetryableConfig
      response?: { status?: number }
    }
    const config = err.config
    const status = err.response?.status

    if (!config || !status || !RETRYABLE_STATUS.includes(status)) throw error
    if ((config.method ?? 'get').toLowerCase() !== 'get') throw error

    const count = config.__retryCount ?? 0
    if (count >= MAX_RETRIES) throw error
    config.__retryCount = count + 1

    // 指数バックオフ + ジッタ。同時に弾かれたリクエストが再び同じ瞬間に揃わないようにする。
    const delay = BASE_DELAY_MS * 3 ** count * (0.5 + Math.random())
    await new Promise((resolve) => setTimeout(resolve, delay))

    return instance.request(config)
  })

  return instance
}

/** アプリ起動時に既定インスタンスへ適用する。 */
export function installApiInterceptors(): void {
  attachAuthInterceptors(axios)
  attachRetryInterceptor(axios)

  // 旧実装が残している「App.Token があればログイン済み」という判定のために、
  // 起動直後にトークンのミラーを温めておく（lib/auth.ts の rememberToken）。
  void getIdToken()
}
